import { NodeTypes, QuestionTypes } from "../utils/constants";
import { NodeField } from "../utils/flowSchema";
import {
  TranslationRowType,
  TranslationField,
  TRANSLATION_FIELD_LIMITS,
} from "../utils/translationConstants";

const nonEmpty = (v) => typeof v === "string" && v.trim().length > 0;

// Which node fields are user-authored free text for a given node.
function nodeFieldKeys(node) {
  if (node.type === NodeTypes.TERMINAL) return [TranslationField.LABEL];
  if (node.type === NodeTypes.QUESTION) {
    return [TranslationField.LABEL, TranslationField.PROMPT, TranslationField.INFORMATION];
  }
  return []; // START — system text, not translated
}

function hydrate(groupKeys, baseValues, valueFor) {
  const translations = {};
  return {
    groupKeys,
    baseTranslations: baseValues,
    fieldLimits: groupKeys.reduce((acc, key) => {
      acc[key] = TRANSLATION_FIELD_LIMITS[key];
      return acc;
    }, {}),
    translations,
    _hydrateLanguages: (languages) => {
      languages.forEach(({ code }) => {
        translations[code] = {
          group: groupKeys.map((key) => ({
            key,
            value: valueFor(code, key) || "",
            maxLength: TRANSLATION_FIELD_LIMITS[key],
          })),
        };
      });
    },
  };
}

// base: { name, information, nodes: [{ id, type, questionType, label, prompt, information, listOptions }] }
// blobByLang: { [lang]: { flow, nodes } } (already parsed)
export function mapFlowToTranslationRows(base, blobByLang, languages) {
  const rows = [];
  const blobOf = (lang) => blobByLang[lang] || { flow: {}, nodes: {} };

  // Flow row
  const flowKeys = [];
  const flowBase = {};
  if (nonEmpty(base.name)) {
    flowKeys.push(TranslationField.NAME);
    flowBase[TranslationField.NAME] = base.name;
  }
  if (nonEmpty(base.information)) {
    flowKeys.push(TranslationField.INFORMATION);
    flowBase[TranslationField.INFORMATION] = base.information;
  }
  if (flowKeys.length > 0) {
    const h = hydrate(flowKeys, flowBase, (lang, key) => blobOf(lang).flow[key]);
    h._hydrateLanguages(languages);
    rows.push({ id: TranslationRowType.FLOW, type: TranslationRowType.FLOW, ...strip(h) });
  }

  (base.nodes || []).forEach((node) => {
    const keys = nodeFieldKeys(node).filter((key) => nonEmpty(node[key]));
    if (keys.length > 0) {
      const nodeBase = keys.reduce((acc, key) => {
        acc[key] = node[key];
        return acc;
      }, {});
      const h = hydrate(keys, nodeBase, (lang, key) => {
        const n = blobOf(lang).nodes[node.id];
        return n ? n[key] : undefined;
      });
      h._hydrateLanguages(languages);
      rows.push({ id: node.id, type: TranslationRowType.NODE, nodeId: node.id, ...strip(h) });
    }

    if (node.type === NodeTypes.QUESTION && node.questionType === QuestionTypes.LIST) {
      (node[NodeField.LIST_OPTIONS] || []).forEach((optionLabel, index) => {
        if (!nonEmpty(optionLabel)) return;
        const h = hydrate(
          [TranslationField.OPTION],
          { [TranslationField.OPTION]: optionLabel },
          (lang) => {
            const n = blobOf(lang).nodes[node.id];
            return n && n.options ? n.options[index] : undefined;
          }
        );
        h._hydrateLanguages(languages);
        rows.push({
          id: `${node.id}#${index}`,
          type: TranslationRowType.OPTION,
          nodeId: node.id,
          optionIndex: index,
          ...strip(h),
        });
      });
    }
  });

  return rows;
}

function strip(h) {
  return {
    groupKeys: h.groupKeys,
    baseTranslations: h.baseTranslations,
    fieldLimits: h.fieldLimits,
    translations: h.translations,
  };
}

// Per-node translation-completion %, averaged across languages (same metric as the modal).
export function translationPercentByNode(nodes, blobByLang, languages) {
  const result = {};
  const languageCount = languages.length || 1;

  (nodes || []).forEach((node) => {
    const data = node.data || {};
    let fields = [];
    // The blob keys options by their original position, so track those indices.
    let optionIndices = [];

    if (node.type === NodeTypes.TERMINAL) {
      if (nonEmpty(data.label)) fields = [TranslationField.LABEL];
    } else if (node.type === NodeTypes.QUESTION) {
      fields = [TranslationField.LABEL, TranslationField.PROMPT, TranslationField.INFORMATION].filter(
        (key) => nonEmpty(data[key])
      );
      if (data.questionType === QuestionTypes.LIST) {
        optionIndices = (data[NodeField.LIST_OPTIONS] || [])
          .map((opt, i) => (nonEmpty(opt) ? i : -1))
          .filter((i) => i >= 0);
      }
    }

    const rowsPerLanguage = (fields.length > 0 ? 1 : 0) + optionIndices.length;
    if (rowsPerLanguage === 0) return;

    let completedRows = 0;
    languages.forEach(({ code }) => {
      const blob = blobByLang[code];
      const nodeBlob = blob && blob.nodes ? blob.nodes[node.id] : null;
      if (!nodeBlob) return;

      if (fields.length > 0 && fields.every((key) => nonEmpty(nodeBlob[key]))) {
        completedRows += 1;
      }
      optionIndices.forEach((idx) => {
        if (nodeBlob.options && nonEmpty(nodeBlob.options[idx])) completedRows += 1;
      });
    });

    result[node.id] = Math.round((completedRows / (rowsPerLanguage * languageCount)) * 100);
  });
  return result;
}
